// ============================================================
// Hatırlatma arayüzü (§5)
// ============================================================

import { card, emptyNote, sectionTitle } from "./ui.js";
import { avatar } from "../lib/avatar.js";
import { donemAdi, esc, fmtDate, money } from "../lib/fmt.js";
import { KANALLAR } from "../domain/notifications.js";
import {
  MERDIVEN,
  hatirlatmaOzeti,
  hatirlatmaTablosu,
  metinUret,
  taksitOzeti,
} from "../domain/reminders.js";

const BASAMAK_RENK = {
  nazik:   "bg-sky-soft text-sky",
  vade:    "bg-sun-soft text-[#B07E14]",
  ikinci:  "bg-coral-soft text-coral",
  yonetim: "bg-coral text-white",
};

export function hatirlatmaSekmesi(branchId) {
  const o = hatirlatmaOzeti(branchId);
  const tablo = hatirlatmaTablosu(branchId);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Açık Fatura", value: o.acikFatura, sub: money(o.toplamAcik), color: "coral" },
      { label: "Hatırlatma Bekleyen", value: o.bekleyenHatirlatma, sub: "gönderilmedi", color: "sun" },
      { label: "Yönetici Seviyesi", value: o.yonetimSeviyesi, sub: "15 günü aşan", color: "coral" },
      { label: "Taksitlendirilmiş", value: o.askidaki, sub: "hatırlatma askıda", color: "teal" },
    ].map(x => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${x.label}</div>
        <div class="font-display font-extrabold text-2xl mt-1 text-${x.color} break-words">${x.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${x.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle("Hatırlatma Merdiveni", `<span class="text-[11px] text-inksoft hidden sm:block">Vade gününe göre</span>`)}
      <div class="flex items-center gap-2 flex-wrap">
        ${MERDIVEN.map(m => `
          <span class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${BASAMAK_RENK[m.id]}">
            ${m.gun < 0 ? `vade ${Math.abs(m.gun)} gün önce` : `vade +${m.gun} gün`} · ${esc(m.label)}
            <span class="opacity-70">${KANALLAR[m.kanal].icon}</span>
          </span>`).join("")}
      </div>
      <p class="text-[11px] text-inksoft mt-2">
        Taksitlendirme planı olan öğrencide hatırlatma askıya alınır —
        anlaşma varken hatırlatma göndermek ilişkiyi bozar.
      </p>
    </div>`, "mb-4")}

  ${card(`
    <div class="p-5">
      ${sectionTitle("Açık Faturalar", `<span class="text-[11px] text-inksoft">${tablo.length} kayıt</span>`)}
      ${tablo.length ? `
        <div class="space-y-2">
          ${tablo.map(h => satir(h, branchId)).join("")}
        </div>` : emptyNote("Açık fatura yok.")}
    </div>`)}`;
}

function satir(h, branchId) {
  const ozet = taksitOzeti(h.plan);

  return `
  <div class="rounded-xl border ${h.askida ? "border-teal/50 bg-teal-soft/20" : h.basamak?.id === "yonetim" ? "border-coral" : "border-line"} p-3">
    <div class="flex items-center gap-3">
      ${avatar(h.student, "w-8 h-8")}
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold truncate">${esc(h.student.name)}</span>
        <span class="block text-[11px] text-inksoft">
          ${donemAdi(h.inv.period)} · vade ${fmtDate(h.inv.due)} ·
          ${h.gecenGun > 0 ? `${h.gecenGun} gün geçti` : h.gecenGun === 0 ? "bugün" : `${Math.abs(h.gecenGun)} gün var`}
        </span>
      </span>
      <span class="text-right shrink-0">
        <span class="block font-display font-bold text-coral">${money(h.kalan)}</span>
        ${h.gonderimSayisi ? `<span class="block text-[10px] text-inksoft">${h.gonderimSayisi} hatırlatma gitti</span>` : ""}
      </span>
    </div>

    ${h.askida && ozet ? `
      <div class="rounded-lg bg-white border border-teal/40 p-2.5 mt-2">
        <div class="flex items-center justify-between gap-2 text-[11px]">
          <span class="font-semibold text-teal">Taksitlendirme planı</span>
          <span class="text-inksoft">${ozet.kalanAdet} taksit kaldı · ${money(ozet.toplam - ozet.odenen)}</span>
        </div>
        ${ozet.siradaki ? `
          <div class="flex items-center justify-between gap-2 text-[11px] mt-1">
            <span class="text-inksoft">Sıradaki: ${ozet.siradaki.no}. taksit · ${fmtDate(ozet.siradaki.dueAt)}</span>
            <span class="flex items-center gap-2 shrink-0">
              <span class="font-semibold">${money(ozet.siradaki.amount)}</span>
              <button data-taksit="${h.plan.id}:${ozet.siradaki.no}"
                class="rounded-lg bg-teal text-white text-[10px] font-semibold px-2 py-1">Tahsil edildi</button>
            </span>
          </div>` : ""}
        ${h.plan.not ? `<div class="text-[10px] text-inksoft mt-1">${esc(h.plan.not)}</div>` : ""}
      </div>`
    : h.basamak ? `
      <div class="mt-2">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-[10px] font-bold rounded-full px-2 py-0.5 ${BASAMAK_RENK[h.basamak.id]}">${esc(h.basamak.label)}</span>
          ${h.gonderildi
            ? `<span class="text-[11px] text-teal font-semibold">✓ Gönderildi${h.sonGonderim ? ` · ${h.sonGonderim.slice(0, 10)}` : ""}</span>`
            : `<button data-hatirlat="${h.student.id}:${h.inv.period}:${h.basamak.id}"
                 class="rounded-lg bg-ink text-white text-[10px] font-semibold px-2.5 py-1 hover:bg-inksoft">
                 ${KANALLAR[h.basamak.kanal].icon} ${KANALLAR[h.basamak.kanal].label} ile gönder
               </button>`}
          <button data-taksitplan="${h.student.id}"
            class="rounded-lg border border-line bg-white text-[10px] font-semibold px-2.5 py-1 hover:bg-paper">
            Taksitlendir
          </button>
        </div>
        <details class="mt-1.5">
          <summary class="text-[10px] text-inksoft cursor-pointer">Gönderilecek metni gör</summary>
          <p class="text-[11px] text-inksoft mt-1 leading-snug bg-paper rounded-lg p-2">
            ${esc(metinUret(h.basamak.id, { student: h.student, inv: h.inv, kalan: h.kalan, branchId }))}
          </p>
        </details>
      </div>`
    : `<div class="text-[11px] text-inksoft mt-2">Vade henüz gelmedi — hatırlatma basamağı yok.</div>`}
  </div>`;
}
